import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ReactElement } from 'react'
import type { Layouts } from 'react-grid-layout'
import type { IBuilderConfig, IGraphBuilderConfig } from '~/types/graph-builder'
import type { IPageLayout } from '~/types/graph-layout'

import { viewClassName, type ViewProps } from '../common-props'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import DynamicDataLayout from '~/components/DynamicDataLayout'
import PageLayout from '~/components/Layouts/Layout'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import {
  keys,
  useBatchCreatePageLayoutMutation,
  useChartConfigsListQuery,
  useListPageLayoutsQuery,
  useUpdatePageLayoutMutation,
} from '~/queries/graphs-builder'
import { CA_PAGES, CA_SECTIONS } from '~/types/graph-builder'
import { handleGenericError } from '~/utils/error'
import { formatConfig } from '~/utils/graphs-builder'
import { getDefaultLayout } from '~/utils/personal-dashboard'
import { displayToast } from '~/utils/toast'

const PAGE = CA_PAGES.OVERVIEW_PUBLISHING

const sortedSections = Object.values(CA_SECTIONS[PAGE])

const PublishingOverview = ({ analyticsApiFn, eventsData, isAppLoading, kpiField }: ViewProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { state } = useAnalyticsContext()

  const { appKey, deviceType } = useAppSummary()

  const isAuthorized = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.LAYOUT_UPDATE.WRITE])

  const layoutClassNames = classNames(viewClassName, { 'filters-collapsed': state.isFiltersCollapsed })

  /** Queries & Mutations */

  const { data: chartConfigs, isLoading: isChartConfigsLoading } = useChartConfigsListQuery({
    appKey,
    deviceType,
    pageName: PAGE,
  })

  const { data: layouts, isLoading: isLayoutsLoading } = useListPageLayoutsQuery({ page: PAGE })

  const { mutateAsync: batchCreateLayout } = useBatchCreatePageLayoutMutation()

  const { mutateAsync: updatePageLayout } = useUpdatePageLayoutMutation()

  /** Query dependent state */

  const formattedConfigs = useMemo(
    () => formatConfig(chartConfigs?.data) as unknown as IGraphBuilderConfig[],
    [chartConfigs?.data]
  )

  const sortedLayouts = useMemo(
    () => layouts?.sort((a, b) => sortedSections.indexOf(a.section || '') - sortedSections.indexOf(b.section || '')),
    [layouts]
  )

  const [layoutsToSave, setLayoutsToSave] = useState<IPageLayout[]>()

  const isLoading = isAppLoading || isChartConfigsLoading || isLayoutsLoading

  /** Handlers */

  const graphOptions = useCallback(
    <T extends IBuilderConfig>(config: T) => ({
      apiSelectionFn: analyticsApiFn,
      appKey,
      config,
      deviceType,
      eventsData,
      isAppLoading,
      kpiField,
    }),
    [analyticsApiFn, appKey, deviceType, eventsData, isAppLoading, kpiField]
  )

  const handleLayoutsChange = (layouts: Layouts, section?: string) =>
    setLayoutsToSave((prev) => prev?.map((el) => (el.section === section ? { ...el, layout: layouts } : el)))

  const handleSaveLayout = async () => {
    if (layoutsToSave) {
      const updatePromises = layoutsToSave.map(({ layout, page_id, section }) =>
        updatePageLayout({ layout, page: page_id, section })
      )

      try {
        await Promise.all(updatePromises)
        await queryClient.invalidateQueries({ queryKey: keys.allLayouts(PAGE) })
        displayToast('Layout successfully saved', 'success')
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  /** Effects */

  useDocumentTitle('CA Overview - Publishing')

  // NOTE: This effect creates automatically non existing section layouts
  useEffect(() => {
    if (formattedConfigs.flat().length > 0) {
      const mappedSections = Object.values(CA_SECTIONS[PAGE]).map((el) =>
        formattedConfigs.filter(({ section }) => el === section)
      )

      const noSectionElements = formattedConfigs.filter(({ section }) => section === '')

      noSectionElements.length > 0 && mappedSections.unshift(noSectionElements)

      const elementsToCreate = mappedSections
        .map((el) => ({
          layout: getDefaultLayout(el),
          section_name: el[0].section,
        }))
        .filter((item) => !layouts?.some((layout) => layout.section === item.section_name))

      if (elementsToCreate.length > 0) {
        const createElement = async (data: {
          page: CA_PAGES
          sections: {
            layout: Layouts
            section_name?: string
          }[]
        }) => {
          await batchCreateLayout(data)
          await queryClient.invalidateQueries({ queryKey: keys.allLayouts(PAGE) })
        }

        createElement({
          page: PAGE,
          sections: elementsToCreate,
        })
      }
    }
  }, [layouts, batchCreateLayout, formattedConfigs, queryClient])

  useEffect(() => {
    if (sortedLayouts) {
      setLayoutsToSave(sortedLayouts)
    }
  }, [sortedLayouts])

  /** Render */

  return (
    <PageLayout className={layoutClassNames} withMargin>
      {isAuthorized ? (
        <div className="save-button-wrapper">
          <Button onClick={handleSaveLayout}>Save Layout</Button>
        </div>
      ) : null}
      <ContentLayout loading={isLoading}>
        {sortedLayouts?.map(({ layout, page_id, section }) => (
          <DynamicDataLayout
            graphConfigs={formattedConfigs.filter(({ section: s }) => s === section)}
            graphOptions={graphOptions}
            key={`${section}-${page_id}`}
            layout={layout}
            onLayoutsChange={handleLayoutsChange}
            sectionName={section}
          />
        ))}
      </ContentLayout>
    </PageLayout>
  )
}

export default PublishingOverview
